/* eslint-disable sonarjs/no-identical-functions */
/* eslint-disable sonarjs/no-duplicate-string */
import Vue from 'vue'
import Router from 'vue-router'
import * as Sentry from '@sentry/vue'
import { applySeoForRoute } from '@/utils/seo.utils'
import Config from '@/config'
import { flags } from '@/utils/abtest.utils'
import store from '@/vuex/store'
import {
  NO_CHAT_ROUTES,
  PUBLIC_ROUTES,
  OPEN_ROUTES_FOR_GUEST,
  ROUTE_NAMES,
  isDestinationRoute,
  EMBED_PRESENTATION_LIST_ROUTES,
} from '@/constant/routes'
import { PPT_ADDIN_EDITOR_ROUTES } from '@/business-logic/ppt-addin-editor-routes'
import { AHA_GUEST_USER } from '@/constant/storageKeys'
import textUtils from '@/utils/textUtil'
// services
import cookie, { AHA_TOKEN_COOKIE } from '@/services/cookie'
import unleashService from '@/services/unleash'
import zoho from '@/services/zoho'
import util from '@/util'
import mixpanelUtils from './utils/mixpanel.utils'
import { registerPrefetchHandler } from '@/vuex/plugins/route-prefetch.plugin'
import { getRoutesToPrefetch } from '@/config/prefetch.config'
import { prefetchMultipleComponents } from '@/utils/prefetch.utils'
import { captureOnboardingUrlOverride } from '@/components-v2/onboarding/onboarding.utils'
import { marketplaceSandboxIdToPreserve } from '@/utils/resolveMarketplaceBase'

Vue.use(Router)
let defaultUrl = ROUTE_NAMES.HOME
if (Config.isEnableAddinPowerpoint) {
  // AHA-43111: NOT the presentation list. A PowerPoint add-in frame boots at the manifest's
  // SourceLocation — root, carrying no presentation id — and which presentation it belongs to
  // is only knowable once Office.js is ready and services/office has read this frame's binding
  // (host-slide tag / document.settings / the read-view bridge). Landing on the list meant
  // every bound frame rendered and fetched a presentation list it was about to replace,
  // flashing it on the way to the editor. The landing route is a neutral spinner that waits
  // for that answer; services/office settles it either way — see settleAddinLandingRoute.
  defaultUrl = ROUTE_NAMES.PPT_ADDIN_LANDING
}

const configRouter = {
  mode: 'history',
  linkExactActiveClass: 'selected',
  linkActiveClass: 'selected',
  routes: [
    {
      path: '/',
      redirect: { name: defaultUrl },
    },
    {
      path: '/inquiries-form',
      name: ROUTE_NAMES.INQUIRIES_FORM,
      component: () => import('./components-v2/my-plan/user-pricing-v3/EnterpriseInquiriesForm.vue'),
    },
    {
      path: '/shared-report/:id/:audienceId?',
      name: ROUTE_NAMES.SHARED_REPORT,
      meta: { title: 'Shared Report', isDestination: true },
      component: () => import('./components-v2/presentation-report/ReportPagePublicWrapper.vue'),
    },
    {
      path: '/apps/onboarding/welcome',
      name: ROUTE_NAMES.ONBOARDING_WELCOME,
      meta: { title: 'Welcome' },
      component: () => import('./components-v2/onboarding/OnboardingAppIframe.vue'),
    },
    {
      // BOO-630: dedicated, shareable redemption page handed to marketplace
      // vendors (AppSumo/StackCommerce). Top-level (NOT under the /apps shell)
      // so it renders header-only with no sidebar. Requires auth (NOT in
      // PUBLIC_ROUTES) — a logged-out buyer is bounced to sign-in and returned.
      path: '/redeem',
      name: ROUTE_NAMES.REDEEM,
      meta: { title: 'Redeem a code' },
      component: () => import('./components-v2/my-plan/redeem/RedeemCodePage.vue'),
      // AHA-46571: FE flag-gate for marketplace lifetime redemption. When off,
      // the page does not exist (matches the marketplace route pattern).
      beforeEnter: (to, from, next) => {
        if (!store.getters['featureToggles/canUseLifetimeOnMarketplace']) {
          next({ name: ROUTE_NAMES.ERROR_404 })
          return
        }
        next()
      },
    },
    {
      path: '/apps/create-blank',
      name: ROUTE_NAMES.CREATE_BLANK,
      component: () => import('./components-v2/create-blank/CreatingBlankPresentationView.vue'),
      meta: { title: 'Creating presentation' },
    },
    {
      path: '/apps/create-from-template',
      name: ROUTE_NAMES.CREATE_FROM_TEMPLATE,
      component: () => import('./components-v2/create-from-template/CreatingPresentationView.vue'),
      meta: { title: 'Creating presentation' },
    },
    {
      // BOO-39: onboarding AI flow lands here (autoOpenChatbot=true&aiPrompt=…&
      // deepLinkSource=onboarding-*). The view creates the first blank
      // presentation, forwards the AI params to the editor, and arms the
      // first-present tour via createNewPresentationAction's deepLinkSource chokepoint.
      path: '/apps/create-from-ai',
      name: ROUTE_NAMES.CREATE_FROM_AI,
      component: () => import('./components-v2/create-from-ai/CreatingPresentationWithAIView.vue'),
      meta: { title: 'Creating presentation' },
    },
    {
      path: '/ms-teams',
      component: () => import('./components-v2/ms-teams/MSTeamsRoot.vue'),
      children: [
        {
          path: 'config',
          name: ROUTE_NAMES.MSTEAMS_TABCONFIG,
          component: () => import('./components-v2/ms-teams/MSTeamsTabConfig.vue'),
        },
        {
          path: 'presenting/:presentationId',
          name: ROUTE_NAMES.MSTEAMS_PRESENTING,
          component: () => import('./components-v2/ms-teams/MSTeamsPresenting.vue'),
        },
      ],
    },
    {
      // making the path not containing 'ms-teams' is intentional
      // since there is existing logic to check 'ms-teams' in path
      //  and trigger some unwanted logic
      path: '/embed',
      component: () => import('./components-v2/embeddable/EmbeddableRoot.vue'),
      children: [
        {
          path: 'presentations',
          name: ROUTE_NAMES.EMBED_SELECT_PRESENTATION,
          component: () => import('./components-v2/embeddable/EmbeddableSelectPresentation.vue'),
        },
        {
          path: 'presentations-v2',
          name: ROUTE_NAMES.EMBED_MY_PRESENTATIONS,
          component: () => import('@/components-v2/embeddable/EmbeddableMyPresentations.vue'),
          // AHA-47221: this list is a page the editor Back arrow may return to, so it has to
          // join the isDestination set (see isDestinationRoute) — the recorder only stores
          // routes carrying this flag, and backV4Action only pushes what the recorder stored.
          // Without it, an add-in Back would resolve to nothing and fall back to the dashboard
          // My Presentations page, which is the page this list exists to replace.
          //
          // Add-in builds only. Route meta is static config, so a bare `true` would ship to the
          // web bundle too, where these same two routes are the MS Teams / host-embedded picker
          // — and there it would silently retarget an editor Back that used to fall back to the
          // dashboard. canUsePptNewUi cannot do this gating (meta is read at route-definition
          // time, before any flag exists), and the build flag is the right grain anyway: with
          // the Unleash flag off the add-in is never sent here, so nothing records it.
          meta: { isDestination: Config.isEnableAddinPowerpoint },
        },
        {
          path: 'presentations-v2/folders/:folderId',
          name: ROUTE_NAMES.EMBED_FOLDER_DETAILS,
          component: () => import('@/components-v2/embeddable/EmbeddableMyPresentations.vue'),
          // Same, per folder and on the same build-only terms: Back returns to the folder the
          // presentation was opened from, matching what FOLDER_DETAIL already does for the
          // dashboard list.
          meta: { isDestination: Config.isEnableAddinPowerpoint },
        },
        // The Shared-with-me half of the same list, reached from the embed menu. Same component
        // as the two above: which presentations it loads is decided by the route NAME (see
        // MyPresentations.202512's isRouteSharedWithMe), exactly as the dashboard's SharedWithMe
        // and ShareFolderDetail are the same component as Presentations and FolderDetail.
        //
        // A shared folder needs its own route rather than reusing EMBED_FOLDER_DETAILS: the
        // folder id alone does not say which listing to fetch, and drilling into a shared folder
        // through the my-presentations route would load the wrong one.
        {
          path: 'shared-presentations-v2',
          name: ROUTE_NAMES.EMBED_SHARED_WITH_ME,
          component: () => import('@/components-v2/embeddable/EmbeddableMyPresentations.vue'),
          meta: { isDestination: Config.isEnableAddinPowerpoint },
        },
        {
          path: 'shared-presentations-v2/folders/:folderId',
          name: ROUTE_NAMES.EMBED_SHARE_FOLDER_DETAILS,
          component: () => import('@/components-v2/embeddable/EmbeddableMyPresentations.vue'),
          meta: { isDestination: Config.isEnableAddinPowerpoint },
        },
        {
          path: 'auth',
          name: ROUTE_NAMES.EMBED_AUTH,
          component: () => import('./components-v2/embeddable/EmbeddableAuth.vue'),
        },
      ],
    },
    {
      path: '/apps/zoom',
      name: ROUTE_NAMES.ZOOM_APP,
      meta: { title: 'AhaSlides Zoom App' },
      component: () => import('./components-v2/zoom/ZoomMainApp.vue'),
    },
    {
      path: '/apps/zoom/presentations',
      name: ROUTE_NAMES.ZOOM_PRESENTATION_LIST,
      meta: { title: 'AhaSlides Zoom App' },
      component: () => import('./components-v2/zoom/ZoomPresentationList.vue'),
    },
    {
      path: '/apps/zoom/audience',
      name: ROUTE_NAMES.ZOOM_AUDIENCE_APP,
      meta: { title: 'Audience Zoom App' },
      component: () => import('./components-v2/zoom/ZoomEmbedAudienceApp.vue'),
    },
    {
      path: '/apps',
      component: () => import('./components-v2/home/Home.vue'),
      children: [
        // AHA-46714: isDestination marks the pages the editor Back arrow may
        // return to — see isDestinationRoute in constant/routes.js. The set is
        // deliberately limited to presentation-browsing surfaces; account,
        // billing, team-settings, marketplace pages and report sub-tabs are
        // excluded by design. A new page joins by carrying isDestination: true.
        {
          path: 'home',
          name: ROUTE_NAMES.HOME,
          meta: { title: 'Home Page', isDestination: true },
          component: () => import('./components-v2/home/HomePageRouter.vue'),
        },
        {
          path: 'trash-bin',
          name: ROUTE_NAMES.TRASH_BIN,
          meta: { title: 'Trash', isDestination: true },
          component: () => import('./components-v2/trash/Trash'),
        },
        {
          path: 'presentations',
          name: ROUTE_NAMES.PRESENTATIONS,
          meta: { title: 'My Presentations', isDestination: true },
          component: () => import('./components-v2/my-presentations/MyPresentations.202512.vue'),
        },
        {
          path: 'shared-presentations',
          name: ROUTE_NAMES.SHARED_WITH_ME,
          meta: { title: 'Shared With Me', isDestination: true },
          component: () => import('./components-v2/my-presentations/MyPresentations.202512.vue'),
        },
        {
          path: 'presentations/folders/:folderId',
          name: ROUTE_NAMES.FOLDER_DETAIL,
          meta: { title: 'My Presentations', isDestination: true },
          component: () => import('./components-v2/my-presentations/MyPresentations.202512.vue'),
        },
        {
          path: 'shared-presentations/folders/:folderId',
          name: ROUTE_NAMES.SHARE_FOLDER_DETAIL,
          meta: { title: 'Shared With Me', isDestination: true },
          component: () => import('./components-v2/my-presentations/MyPresentations.202512.vue'),
        },
        {
          path: 'team/workspace/folders/:folderId',
          name: ROUTE_NAMES.TEAM_FOLDER_DETAIL,
          meta: { title: 'Team Presentations' },
          component: () => import('./components-v2/my-presentations/MyPresentations.202512.vue'),
        },
        {
          path: 'template-library',
          name: ROUTE_NAMES.TEMPLATE_LIBRARY,
          meta: { title: 'Templates', isDestination: true },
          component: () => import('./components-v2/template-library/TemplateLibrary.toggle.vue'),
        },
        {
          path: 'addons/:appId/:path(.*)*',
          name: ROUTE_NAMES.EMBED_APP,
          meta: { title: 'Embed App' },
          component: () => import('./components-v2/embed-app/EmbedAppContainer.vue'),
          props: true,
        },
        {
          path: 'team/workspace',
          name: ROUTE_NAMES.TEAM,
          meta: { title: 'My Team' },
          component: () => import('./components-v2/my-presentations/MyPresentations.vue'),
        },
        {
          path: 'team/members',
          name: ROUTE_NAMES.TEAM_MEMBER,
          meta: { title: 'My Team' },
          component: () => import('./components-v2/my-team/Team.vue'),
        },
        {
          path: 'team/settings',
          name: ROUTE_NAMES.TEAM_SETTINGS,
          meta: { title: 'Team Settings' },
          component: () => import('./components-v2/my-team/TeamSettings.vue'),
        },
        {
          path: 'user-profile',
          name: ROUTE_NAMES.USER_PROFILE,
          meta: { title: 'Account settings' },
          component: () => import('./components-v2/my-profile/MyProfile.202512.vue'),
        },
        // AHA-47349-presenter-can-create-new-slide-type
        {
          path: 'developer',
          name: ROUTE_NAMES.DEVELOPER,
          meta: { title: 'Developer' },
          component: () => import('./components-v2/developer/DeveloperDomains.vue'),
          beforeEnter: async (to, from, next) => {
            // Feature flags are empty until Unleash is ready; on a hard reload or a
            // pasted /apps/developer link the getter would be falsy and bounce to
            // Home. Wait for readiness first so the guard sees the real flag value.
            try {
              if (unleashService.ready) await unleashService.ready
            } catch (e) {
              // ready never rejects; the flag check below still runs regardless.
            }
            if (store.getters['featureToggles/canUseCreateNewSlideType']) {
              next()
            } else {
              next({ name: ROUTE_NAMES.HOME })
            }
          },
        },
        {
          path: 'billing-and-payments',
          name: ROUTE_NAMES.BILLING_LIST,
          meta: { title: 'Payment' },
          component: () => import('./components-v2/billing/BillingList.vue'),
        },
        {
          path: 'billing-and-payments/:id',
          name: ROUTE_NAMES.BILLING_DETAIL,
          meta: { title: 'Billing detail' },
          component: () => import('./components-v2/billing/BillingDetail.vue'),
        },
        {
          path: 'community',
          name: ROUTE_NAMES.COMMUNITY,
          meta: { title: 'Community' },
          component: () => import('./components-v2/community/UserCommunity.vue'),
        },
        {
          path: 'upgrade/bulk-checkout-success',
          name: ROUTE_NAMES.UPGRADE_BULK_SUCCESS,
          meta: { title: 'Bulk Checkout Success' },
          component: () => import('./components-v2/my-plan/BulkCheckoutSuccess.vue'),
        },
        {
          path: 'upgrade/checkout-success',
          name: ROUTE_NAMES.UPGRADE_SUCCESS,
          meta: { title: 'Checkout Success' },
          component: () => import('./components-v2/my-plan/CheckoutSuccess.vue'),
        },

        {
          path: 'upgrade/:id',
          name: ROUTE_NAMES.UPGRADE,
          meta: { title: 'Upgrade Plan' },
          component: () => import('./components-v2/my-plan/UpgradePlan.vue'),
          beforeEnter: (to, from, next) => util.landingToPricingRouter(to, from, next),
        },
        {
          path: 'stripe-transition',
          name: ROUTE_NAMES.STRIPE_TRANSITION,
          component: () => import('./components-v2/my-plan/StripeTransition.vue'),
        },
        {
          path: 'direct-bulk-purchase',
          name: ROUTE_NAMES.DIRECT_BULK_PURCHASE,
          component: () => import('./components-v2/my-plan/DirectBulkPurchase.vue'),
        },

        {
          path: 'user-plan',
          name: ROUTE_NAMES.USER_PLAN,
          meta: { title: 'My Plan' },
          component: () => import('./components-v2/my-plan/UserPlan.vue'),
        },
        {
          path: 'report/questions',
          name: ROUTE_NAMES.REPORT_QUESTION,
          meta: { title: 'Reports' },
          component: () => import('./components-v2/report-session/ReportIframeWrapper.vue'),
        },
        {
          path: 'report/slides',
          name: ROUTE_NAMES.REPORT_SLIDES,
          meta: { title: 'Reports' },
          component: () => import('./components-v2/report-session/ReportIframeWrapper.vue'),
        },
        {
          // AHA-43228: host route for the report's Surveys tab. Named to match the
          // report app's `SurveysReport` route so its pushParent({ name }) resolves
          // here (same pattern as the other report tabs); the iframe path is derived
          // from this route's fullPath, loading the report's /reports/surveys page.
          path: 'report/surveys',
          name: ROUTE_NAMES.REPORT_SURVEYS,
          meta: { title: 'Reports' },
          component: () => import('./components-v2/report-session/ReportIframeWrapper.vue'),
        },
        {
          path: 'report/participants',
          name: ROUTE_NAMES.ALL_PARTICIPANTS,
          meta: { title: 'Reports' },
          component: () => import('./components-v2/report-session/ReportIframeWrapper.vue'),
        },
        {
          path: 'report/participant-profile/responses',
          name: ROUTE_NAMES.ALL_PARTICIPANT_RESPONSES,
          meta: { title: 'Participant Profile' },
          component: () => import('./components-v2/report-session/ReportIframeWrapper.vue'),
        },
        {
          path: 'report/:id/:audienceId?',
          name: ROUTE_NAMES.PRESENTATION_REPORT,
          meta: { title: 'Session Report', isDestination: true },
          component: () => import('./components-v2/presentation-report/SessionReportPage.vue'),
        },
        {
          path: 'marketplace',
          name: ROUTE_NAMES.MARKETPLACE,
          meta: { title: 'Marketplace' },
          component: () => import('./components-v2/integrations/IntegrationsWrapper.vue'),
          beforeEnter: (to, from, next) => {
            const isPresenterWhiteLabel = util.checkWhiteLabelDomain()
            const hasAccess = store.state.currentUser.identifyProvider !== 'skoletube' && !isPresenterWhiteLabel
            if (!hasAccess) {
              next({ name: ROUTE_NAMES.ERROR_404 })
              return
            }
            next()
          },
        },
        {
          path: 'marketplace/:id',
          name: ROUTE_NAMES.MARKETPLACE_DETAIL,
          meta: { title: 'Marketplace Detail' },
          component: () => import('./components-v2/integrations/IntegrationsWrapper.vue'),
        },
        {
          path: 'connect/:platform',
          name: ROUTE_NAMES.INTEGRATION_CONNECT,
          meta: { title: 'Connect Integration', isDestination: true },
          component: () => import('./components-v2/integrations/IntegrationMethodsPanel.vue'),
        },
        {
          path: 'white-label',
          name: ROUTE_NAMES.WHITE_LABEL,
          meta: { title: 'White Label configuration' },
          component: () => import('./components-v2/white-label/WhiteLabel.vue'),
        },
        {
          path: 'referral',
          name: ROUTE_NAMES.REFERRAL,
          meta: { title: 'Refer a Friend' },
          component: () => import('./components-v2/referral/Referral.vue'),
        },
        {
          path: 'admin-analytics',
          name: ROUTE_NAMES.ADMIN_ANALYTICS,
          meta: { title: 'Admin Analytics' },
          component: () => import('./components-v2/admin-analytics/AdminAnalyticalWrapper.vue'),
        },
        {
          path: 'report',
          name: ROUTE_NAMES.REPORT_SESSION,
          meta: { title: 'Reports' },
          component: () => import('./components-v2/report-session/ReportIframeWrapper.vue'),
        },
      ],
    },
    {
      path: '/demo',
      name: ROUTE_NAMES.DEMO_PRESENTATION,
      component: () => import('./components-v2/presentation-editor/demo-mode/DemoPage.vue'),
    },
    {
      path: '/pages/sso',
      name: ROUTE_NAMES.SSO,
      meta: { title: 'Login with SSO' },
      component: () => import('./components-v2/auth/SSOV3.vue'),
    },
    {
      path: '/auth',
      name: ROUTE_NAMES.AUTH,
      meta: { title: 'Login' },
      component: () => import('./components-v2/common/auth/Auth.vue'),
    },
    {
      path: '/microsoft-auth',
      name: ROUTE_NAMES.MICROSOFT_AUTH,
      component: () => import('./components-v2/common/auth/MicrosoftRedirect.vue'),
    },
    {
      path: '/pages',
      redirect: '/pages/register',
    },
    {
      path: '/apps/community',
      name: ROUTE_NAMES.COMMUNITY,
    },
    {
      path: '/pages/login',
      name: ROUTE_NAMES.LOGIN,
      meta: { title: 'Login' },
      component: () => import('./components-v2/auth/LoginV3.vue'),
    },
    {
      path: '/pages/oauth',
      name: ROUTE_NAMES.OAUTH,
      meta: { title: 'Login' },
      component: () => import('./components-v2/auth/OAuth.vue'),
    },
    {
      path: '/pages/confirm-third-party-access',
      name: ROUTE_NAMES.CONFIRM_OPENAI,
      meta: { title: 'confirm-third-party-access' },
      component: () => import('@/components-v2/confirm-third-party-access/index.vue'),
    },
    {
      path: '/pages/device',
      name: ROUTE_NAMES.DEVICE_GRANT,
      meta: { title: 'Connect your app' },
      component: () => import('@/components-v2/auth/device/DeviceGrant.vue'),
    },
    {
      path: '/pages/register',
      name: ROUTE_NAMES.REGISTER,
      meta: { title: 'Create your account' },
      component: () => import('./components-v2/auth/RegisterV3.vue'),
    },
    {
      path: '/pages/forgot-password',
      name: ROUTE_NAMES.FORGOT_PASSWORD,
      meta: { title: 'Forgot password' },
      component: () => import('./components-v2/auth/ForgotPasswordV3.vue'),
    },
    {
      path: '/pages/token',
      name: ROUTE_NAMES.TOKEN,
      component: () => import('./components-v2/token/Token.vue'),
    },
    {
      // Public landing for the link in the verification email
      // (`${BASE_URL}/verify-email?token=...` issued by general-api).
      path: '/verify-email',
      name: ROUTE_NAMES.VERIFY_EMAIL,
      meta: { title: 'Verify your email' },
      component: () => import('./components-v2/auth/VerifyEmail.vue'),
    },
    {
      path: '/pages/error-404',
      name: ROUTE_NAMES.ERROR_404,
      meta: { title: 'Page not found' },
      component: () => import('./components-v2/404/Error404.vue'),
    },
    {
      path: '/pages/service-unavailable',
      name: ROUTE_NAMES.SERVICE_UNAVAILABLE,
      meta: { title: 'Service unavailable' },
      component: () => import('./components-v2/error/ServiceUnavailable.vue'),
    },
    {
      path: '/pages/clear-token',
      name: ROUTE_NAMES.CLEAR_TOKEN,
      component: () => import('./components-v2/token/ClearToken.vue'),
    },
    {
      path: '/referral/existing-user',
      name: 'ReferralExistingUser',
      component: () => import('./components-v2/referral/ReferralExistingUser.vue'),
      meta: { title: 'Referral - Existing User' },
    },
    {
      path: '/referral/invalid',
      name: 'ReferralInvalidLink',
      component: () => import('./components-v2/referral/ReferralInvalidLink.vue'),
      meta: { title: 'Invalid Referral Link' },
    },
    {
      path: '*',
      redirect: { name: ROUTE_NAMES.ERROR_404 },
    },
    {
      path: '/presentation/:id',
      name: ROUTE_NAMES.PRESENTATION_DETAIL,
      component: () => import('./components-v2/presentation-editor/PresentationEditorMultiSessionWrapper.vue'),
    },
    {
      // AHA-47221: the add-in's editor entrance. It mounts exactly what /presentation/:id
      // mounts — the id is in the path for the same reason it is there, since
      // PresentationEditorMultiSessionWrapper keys the editor off $route.params.id.
      //
      // A route rather than a store flag: "the pane is showing the editor" is part of where the
      // user IS, PowerPoint reloads the task pane on its own schedule, and a URL survives that.
      // See business-logic/ppt-addin-editor-routes.
      path: '/ppt-addin/edit-slides/:id',
      name: ROUTE_NAMES.PPT_ADDIN_EDIT_SLIDES,
      component: () => import('./components-v2/presentation-editor/PresentationEditorMultiSessionWrapper.vue'),
    },
    {
      path: '/slide-selector/:presentationId',
      name: ROUTE_NAMES.PPT_SLIDE_SELECTOR,
      meta: { title: 'Select slides' },
      component: () => import('./components-v2/ppt-slide-selector/PptSlideSelector.vue'),
    },
    {
      // AHA-43111: where an add-in frame waits while services/office works out which
      // presentation it is bound to. Deliberately NOT under /apps — that subtree mounts the
      // dashboard shell (sidebar, workspace bar, presentation fetches), which is the cost this
      // route exists to avoid paying on a frame that is on its way to the editor.
      path: '/ppt-addin',
      name: ROUTE_NAMES.PPT_ADDIN_LANDING,
      meta: { title: 'AhaSlides' },
      component: () => import('./components-v2/ppt-addin/PptAddinLanding.vue'),
    },
    {
      path: '/casting/:id/:shareCode',
      name: ROUTE_NAMES.PRESENTATION_DETAIL_PUBLIC,
      component: () => import('./components-v2/presentation-editor/CastingScreen.vue'),
      // The casting screen's editor-vs-presenting render path keys off the
      // ?presenting=true query param. The casting URL is always meant to
      // render the presenting view (the paused-overlay handles the paused
      // state), so force the flag onto the URL before the component loads.
      beforeEnter: (to, from, next) => {
        if (to.query.presenting === 'true') {
          next()
        } else {
          next({
            ...to,
            query: { ...to.query, presenting: 'true' },
            replace: true,
          })
        }
      },
    },
    {
      path: '/presentation-invite/:id',
      name: ROUTE_NAMES.PRESENTATION_JOIN_VIA_TOKEN,
      component: () => import('./components-v2/presentation-editor/collaboration/JoinPresentationViaToken.vue'),
    },
    {
      path: '/presentation-google-slide/:id',
      name: ROUTE_NAMES.PRESENTATION_DETAIL_GOOGLE_SLIDE,
      component: () => import('./components-v2/presentation-editor/PresentationEditorGoogleSlidePage.vue'),
    },
    {
      path: '/presentation-google-slide-error',
      name: ROUTE_NAMES.PRESENTATION_ERROR_GOOGLE_SLIDE,
      component: () => import('./components-v2/presentation-editor/PresentationErrorGoogleSlidePage.vue'),
    },
    {
      path: '/presentation/:id/backstage',
      name: ROUTE_NAMES.PRESENTATION_BACKSTAGE,
      component: () => import('./components-v2/presentation-editor/PresentationEditorWrapper.vue'),
    },
    {
      path: '/share/:id',
      name: ROUTE_NAMES.PRESENTATION_SHARE,
      component: () => import('./components-v2/presentation-editor/PresentationSharingView.vue'),
      meta: { noindex: true },
    },
    {
      path: '/shared-template/:id',
      name: ROUTE_NAMES.TEMPLATE_VIEW,
      meta: { isDestination: true },
      component: () => import('./components-v2/template/TemplateView.vue'),
    },
    {
      path: '/template/:shareCode',
      name: ROUTE_NAMES.TEMPLATE_PREVIEW,
      meta: { isDestination: true },
      component: () => import('./components-v2/template/TemplatePreview.vue'),
    },
    {
      path: '/live-demo/',
      name: ROUTE_NAMES.PRESENTATION_PLAY_DEMO,
      component: () => import('./components-v2/presentation-play-demo/PresentationPlayDemo.vue'),
    },
    {
      path: '/save-presentation/',
      name: ROUTE_NAMES.SAVE_PRESENTATION,
      component: () => import('./components-v2/save-presentation/SavePresentation.vue'),
    },
    {
      path: '/google-redirect',
      name: ROUTE_NAMES.GOOGLE_REDIRECT,
      component: () => import('./components-v2/common/auth/GoogleRedirect.vue'),
    },
    {
      path: '/google-auth',
      name: ROUTE_NAMES.GOOGLE_AUTH,
      component: () => import('./components-v2/common/auth/GoogleAuthView.vue'),
    },
    {
      path: '/integration-google-slide',
      name: 'IntegrationGoogleSlide',
      component: () => import('./components-v2/integrations/googleSlide.vue'),
    },
    {
      path: '/integration-google-slide-preseting',
      name: 'IntegrationGoogleSlidePresenting',
      component: () => import('./components-v2/integrations/googleSlidePresenting.vue'),
    },
    {
      path: '/google-drive-shortcut',
      name: ROUTE_NAMES.GOOGLE_DRIVE_SHORTCUT,
      component: () => import('./components-v2/common/auth/GoogleDriveShortcut.vue'),
    },
    {
      path: '/zoho-auth',
      name: ROUTE_NAMES.ZOHO_AUTH,
      component: () => import('./components-v2/zoho-auth/ZohoAuth.vue'),
    },
    {
      path: '/pages/presenting-experience-survey/:presentationId',
      name: ROUTE_NAMES.PRESENTING_EXPERIENCE_SURVEY,
      meta: { title: 'Presenting Experience Survey' },
      component: () => import('./components-v2/crazy-egg/PresentingExperienceSurvey.vue'),
    },
  ],
}

if (Config.basePath) {
  configRouter.base = `${Config.basePath}/`
}

if (Config.integrationPlatform === 'GoogleSlide') {
  delete configRouter.base
  configRouter.routes = configRouter.routes.filter((item) => {
    return item.path !== '/' && item.path !== '*'
  })
}

// AHA-46329: the slide selector only exists inside the PowerPoint add-in — it drives
// Office.js APIs that no other host has.
// AHA-43111: same for the add-in landing route — off the add-in build nothing ever navigates
// to it (defaultUrl stays HOME), so registering it would only add a dead URL to the main app.
// AHA-47221: and the same for the add-in editor entrance — nothing outside the add-in build
// ever pushes it.
const ADD_IN_ONLY_ROUTES = [
  ROUTE_NAMES.PPT_SLIDE_SELECTOR,
  ROUTE_NAMES.PPT_ADDIN_LANDING,
  ...PPT_ADDIN_EDITOR_ROUTES,
]
if (!Config.isEnableAddinPowerpoint) {
  configRouter.routes = configRouter.routes.filter((item) => {
    return !ADD_IN_ONLY_ROUTES.includes(item.name)
  })
}

const router = new Router(configRouter)

// Keep ?marketplaceSandboxId= on the URL across internal navigation (home /
// presentation list → detail → presenting), so a dev-facing build stays aimed at
// the same marketplace sandbox after a link is clicked and survives F5. Registered
// FIRST and it redirects immediately, so the heavier guards below run only once,
// on the redirected navigation that already carries the param. Inert on production
// builds and whenever the destination sets the param itself (including an empty
// reset). See marketplaceSandboxIdToPreserve.
router.beforeEach((to, from, next) => {
  const id = marketplaceSandboxIdToPreserve(from.query, to.query, Config.presenterDomain)
  if (id) {
    // `to.path` already carries the resolved route params, so don't also pass
    // `params` — Vue Router ignores (and warns about) params when a path is given.
    next({ path: to.path, query: { ...to.query, marketplaceSandboxId: id }, hash: to.hash })
    return
  }
  next()
})

// Capture ?onboarding= BEFORE any redirect fires so the param survives the full
// redirect chain (e.g. / → /presentations → /presentation/:id). OnboardingOverlay
// reads it from sessionStorage when the param is no longer on the live $route.query.
router.beforeEach((to, from, next) => {
  if (to.query && to.query.onboarding) {
    captureOnboardingUrlOverride(`?onboarding=${to.query.onboarding}`)
  }
  next()
})

// eslint-disable-next-line sonarjs/cognitive-complexity
router.beforeEach((to, from, next) => {
  const {
    name, query, params, redirectedFrom, fullPath, path,
  } = to

  // cache from
  store.dispatch('presentationEditor/setPreviousRouteAction', from)
  store.dispatch('movePresentation/updateIsAllPresentationsSelected', false)

  if (to.name === 'Team' || to.name === 'TeamFolderDetail') {
    router.push({ name: 'Presentations' })
  }

  if (typeof to.meta === 'function') {
    const { title } = to.meta()

    util.setDocumentTitle(title)
  }

  if (to.meta && typeof to.meta !== 'function' && to.meta.title) {
    util.setDocumentTitle(to.meta.title)
  }

  if (to.name === 'ZoomApp') {
    next()
  }

  const token = cookie.getValue(AHA_TOKEN_COOKIE)
  const url = `${document.location.origin}${fullPath}`

  store.commit('SET_ROUTE', to)
  mixpanelUtils.track(`PV > ${name}`, {
    fullPath,
    ...params,
  })
  if (from?.name) {
    // register previous route in context
    // can be helpful when tracking editor load time coming from
    // different pages
    mixpanelUtils.register({
      previousPage: from.name,
      previousPagePath: from.fullPath,
    })
  } else {
    mixpanelUtils.unregister('previousPage')
    mixpanelUtils.unregister('previousPagePath')
  }

  if (name === 'Register') {
    util.trackGA4AndMixpanel('register_page_view', {
      fullPath,
      ...params,
    })
  } else if (name === ROUTE_NAMES.PRESENTATION_DETAIL) {
    // track presentation editor load
    mixpanelUtils.timeEvent('Presentation editor loaded > since router changed')
  }

  if (window.sendinblue) {
    window.sendinblue.page(name, {
      ma_url: url,
      title: name,
      ma_title: name,
      ma_path: fullPath,
    })
  }

  const isEmbed = util.getParamValue('embedded')

  if (
    !isEmbed &&
    NO_CHAT_ROUTES.indexOf(name) < 0 &&
    !path.includes('/ms-teams')
  ) {
    zoho.init()
  }
  const isGuest = cookie.getValue(AHA_GUEST_USER)
  if (util.isLoggedIn()) {
    // Retires itself: once the cookie is httpOnly there is no readable token to send.
    if (token) {
      Vue.http.headers.common.Authorization = `Bearer ${token}`
    }
    store.dispatch('setAuthAction', true)
  }

  if (Config.integrationPlatform === 'GoogleSlide') {
    next()
    return
  }

  // AHA-47221: the embed presentation list is public for the MS Teams side panel, but it is also
  // the PowerPoint ADD-IN's own presentation list, and an add-in frame that reaches it signed out
  // renders an empty page with no prompt and no route out. Only the add-in build takes it back
  // out of the public set, so nothing changes for the host-embedded picker.
  //
  // Gated on the BUILD flag rather than canUsePptNewUi: Unleash resolves after page load, and
  // this guard runs on the first navigation, so a getter here would let exactly the cold-load
  // case through. It costs nothing to be broader — with the flag off the add-in is never sent to
  // this list at all (see pptAddinListRouteName), so the term can only fire where it is wanted.
  const isPptAddinEmbedList = Config.isEnableAddinPowerpoint
    && EMBED_PRESENTATION_LIST_ROUTES.indexOf(name) > -1
  const isPublicRoute = PUBLIC_ROUTES.indexOf(name) > -1 && !isPptAddinEmbedList

  if (
    !isPublicRoute
    && !store.state.isAuthenticated
  ) {
    query.redirect = to.fullPath
  }

  if (from.name === 'Presentations' && !cookie.getValue('hideOnboardingTooltip')) {
    cookie.setValue('hideOnboardingTooltip', true)
  }

  if (isPublicRoute) {
    next()
    return
  }

  // if logged in as normal user, open all routes
  if (util.isLoggedIn() && !isGuest) {
    next()
    return
  }
  if (isGuest) {
    // Same subtraction as isPublicRoute, for the same reason: OPEN_ROUTES_FOR_GUEST is
    // PUBLIC_ROUTES plus one, so without this a guest add-in frame would reach the list the
    // block above just closed to signed-out ones — and see the same empty page, since the
    // presentations on it belong to a real account.
    if (OPEN_ROUTES_FOR_GUEST.indexOf(name) > -1 && !isPptAddinEmbedList) {
      next()
      return
    }
    router.push({ path: '/pages/register', query })
    return
  }

  if (name === 'Upgrade') {
    if (params.id === 'essential-monthly') {
      query.content = 'essential_monthly'
    }

    if (params.id === 'essential-yearly') {
      query.content = 'essential_annually'
    }

    if (params.id === 'pro-monthly') {
      query.content = 'pro_monthly'
    }

    if (params.id === 'pro-yearly') {
      query.content = 'pro_annually'
    }

    if (query.content) {
      query.source = 'pricing'
    }
  }

  // https://ahaslides.atlassian.net/browse/AHA-3127
  // if user without account is invited, redirect to signup
  if (query.edit || query['edit-folder']) {
    try {
      const decryptedQuery = textUtils.decryptText(query.edit) || textUtils.decryptText(query['edit-folder'])
      const inviteCode = util.getParamValue('invite-code', decryptedQuery)
      query['invite-code'] = inviteCode
    } catch (err) {
      Sentry.captureException(err)
    }
  }
  // PowerPoint add-in with a configured device-grant client lands on the OAuth
  // device sign-in page, ahead of the signup/login redirects below (so it works
  // even from root `/`, where redirectedFrom would otherwise force signup).
  if (Config.isEnableAddinPowerpoint && Config.powerpointOAuthClientId && flags.canUseOauthLogin) {
    router.push({ path: '/pages/oauth', query })
    return
  }
  if (
    (redirectedFrom &&
      (redirectedFrom === '/' || redirectedFrom.indexOf('/?') === 0)) ||
    query['invite-code']
  ) {
    router.push({ path: '/pages/register', query })
    return
  }
  if (path.includes('ms-teams')) {
    router.replace({ name: 'MSTeams-TabConfig', query })
  } else {
    router.push({ path: '/pages/login', query })
  }

  next()
})

router.beforeEach((to, _, next) => {
  applySeoForRoute(to)
  next()
})

router.afterEach((to, from) => {
  // wait until the route is loaded before removing inital polling loader
  // to avoid brief blank screen
  store.dispatch('routeChangeAction', {
    to,
    from,
  })
  util.removeInitialLoader()
})

// AHA-46714: record where the editor Back arrow returns to. afterEach, not
// beforeEach: it must see the route actually landed on after guard redirects.
// to.path, never fullPath: a recorded query would replay one-shot params
// (?onboarding=...) on Back. Not flag-gated, and it must stay that way:
// Unleash flags load async, so any gate here would drop the first navigation
// of every page load (the most common Back flow). Under the AHA-46714
// rollback flag this state simply loses its only reader (see
// canUseLegacyEditorBack), so recording stays inert rather than wrong.
router.afterEach((to) => {
  if (!isDestinationRoute(to)) return
  try {
    store.dispatch('presentationEditor/setLastDestinationAction', to.path)
  } catch (e) {
    // Best-effort: a recorder failure must never break a navigation.
    Sentry.captureException(e)
  }
})

router.onError(() => {
  store.dispatch('setIsServerConnectableAction', false)
})

// Register prefetch handler with the route-prefetch plugin
// This avoids circular dependency while keeping all plugins declared in store.js
const allRoutes = router.options.routes.flatMap((r) => [r, ...(r.children || [])])
const routeMap = allRoutes.reduce((map, route) => {
  if (route.name) {
    map[route.name] = route
  }
  return map
}, {})

registerPrefetchHandler((currentRouteName) => {
  const routeNamesToPrefetch = getRoutesToPrefetch(currentRouteName)

  if (routeNamesToPrefetch.length === 0) {
    return
  }

  const componentImportFns = routeNamesToPrefetch
    .map((routeName) => {
      const route = routeMap[routeName]
      return route?.component && typeof route.component === 'function' ? route.component : null
    })
    .filter(Boolean)

  if (componentImportFns.length > 0) {
    prefetchMultipleComponents(componentImportFns)
  }
})

export default router
